import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { Grid } from "@giphy/react-components";
import type { IGif } from "@giphy/js-types";
import { useCallback, useEffect, useMemo, useState, type SyntheticEvent } from "react";
import type { GifsResult } from "@giphy/js-fetch-api";
import { getGiphyApiKey } from "../config/giphyEnv";

const EMPTY_GIFS: Promise<GifsResult> = Promise.resolve({
  data: [],
  pagination: { count: 0, total_count: 0, offset: 0 },
  meta: { status: 200, msg: "OK", response_id: "" },
});

type GiphyCatalog = "gifs" | "stickers";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called with GIPHY media id (validated server-side). */
  onPick: (giphyId: string) => void;
};

export function TableGiphyPickerDialog({ open, onClose, onPick }: Props) {
  const apiKey = getGiphyApiKey();
  const gf = useMemo(() => (apiKey ? new GiphyFetch(apiKey) : null), [apiKey]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [catalog, setCatalog] = useState<GiphyCatalog>("gifs");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const fetchGifs = useCallback(
    (offset: number) => {
      if (!gf) return EMPTY_GIFS;
      const opts = { offset, limit: 12, rating: "pg" as const, type: catalog };
      if (debouncedSearch) {
        return gf.search(debouncedSearch, opts);
      }
      return gf.trending(opts);
    },
    [gf, debouncedSearch, catalog]
  );

  const onGifClick = useCallback(
    (gif: IGif, e: SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      onPick(String(gif.id));
    },
    [onPick]
  );

  const gridWidth = typeof window !== "undefined" ? Math.min(420, window.innerWidth - 48) : 360;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>GIF or sticker to table</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {apiKey ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={catalog}
            onChange={(_, v: GiphyCatalog | null) => v && setCatalog(v)}
            sx={{ mb: 1.5, display: "flex", width: "100%", "& .MuiToggleButton-root": { flex: 1 } }}
          >
            <ToggleButton value="gifs">GIFs</ToggleButton>
            <ToggleButton value="stickers">Stickers</ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        <TextField
          fullWidth
          size="small"
          label={catalog === "stickers" ? "Search stickers" : "Search GIFs"}
          placeholder="Type to search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
          helperText={apiKey ? undefined : "Add REACT_APP_GIPHY_API_KEY to .env and restart dev server."}
          sx={{ mb: 1.5 }}
        />
        {!apiKey ? (
          <Typography color="text.secondary" variant="body2" sx={{ pb: 1 }}>
            Free key:{" "}
            <Link href="https://developers.giphy.com/dashboard/" target="_blank" rel="noreferrer">
              developers.giphy.com
            </Link>
          </Typography>
        ) : (
          <>
            <Box sx={{ maxHeight: "min(55vh, 420px)", overflow: "auto" }}>
              <Grid
                key={`${catalog}-${debouncedSearch || "__trending__"}`}
                width={gridWidth}
                columns={3}
                gutter={6}
                fetchGifs={fetchGifs}
                onGifClick={onGifClick}
                hideAttribution={false}
              />
            </Box>
            <Typography variant="caption" component="div" sx={{ mt: 1.5, textAlign: "center", opacity: 0.75 }}>
              <Link href="https://giphy.com" target="_blank" rel="noreferrer">
                Powered by GIPHY
              </Link>
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
